/**
 * Polling hook that tracks plugin publish pipeline status.
 * Polls GET /plugins/submissions/:id every 3s until a terminal state is reached.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import api from '@/lib/api';

export interface PublishStatus {
  submissionId: string | null;
  status: 'reviewing' | 'building' | 'published' | 'rejected' | 'failed' | null;
  reviewStatus: string | null;
  reviewFindings: unknown[] | null;
  errorMessage: string | null;
  archId: string | null;
  isPolling: boolean;
}

const TERMINAL_STATUSES = new Set(['published', 'rejected', 'failed']);
const POLL_INTERVAL_MS = 3_000;

const INITIAL_STATUS: PublishStatus = {
  submissionId: null,
  status: null,
  reviewStatus: null,
  reviewFindings: null,
  errorMessage: null,
  archId: null,
  isPolling: false,
};

export function usePublishStatus(submissionId: string | null): PublishStatus {
  const [status, setStatus] = useState<PublishStatus>(INITIAL_STATUS);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelledRef = useRef(false);

  const poll = useCallback(async (id: string) => {
    if (cancelledRef.current) return;
    try {
      const result = await api.getPluginSubmission(id);
      if (cancelledRef.current) return;

      const sub = result.submission;
      const review = result.review;

      const allFindings = [
        ...((review?.codeFindings as unknown[]) ?? []),
        ...((review?.dependencyFindings as unknown[]) ?? []),
        ...((review?.policyFindings as unknown[]) ?? []),
        ...((review?.qualityFindings as unknown[]) ?? []),
      ];

      const newStatus: PublishStatus = {
        submissionId: id,
        status: sub.status as PublishStatus['status'],
        reviewStatus: review?.status ?? null,
        reviewFindings: allFindings.length > 0 ? allFindings : null,
        errorMessage: sub.errorMessage,
        archId: sub.archId,
        isPolling: !TERMINAL_STATUSES.has(sub.status),
      };

      setStatus(newStatus);

      if (!TERMINAL_STATUSES.has(sub.status) && !cancelledRef.current) {
        timeoutRef.current = setTimeout(() => poll(id), POLL_INTERVAL_MS);
      }
    } catch {
      // On error, keep polling — transient failures shouldn't stop tracking
      if (!cancelledRef.current) {
        timeoutRef.current = setTimeout(() => poll(id), POLL_INTERVAL_MS);
      }
    }
  }, []);

  useEffect(() => {
    if (!submissionId) {
      setStatus(INITIAL_STATUS);
      return;
    }

    cancelledRef.current = false;
    setStatus({
      ...INITIAL_STATUS,
      submissionId,
      status: 'reviewing',
      isPolling: true,
    });

    // Start polling
    poll(submissionId);

    return () => {
      cancelledRef.current = true;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [submissionId, poll]);

  return status;
}
